import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  bubbleAnchor,
  isBubbleFailed,
} from '@/components/admin/sessions/conversation/conversation-fil';
import { isTicked, pageExit, pageWindowLabel } from '@/components/admin/sessions/walk/walk-labels';
import { WalkNoyauSection } from '@/components/admin/sessions/walk/walk-noyau-section';
import { costLabel, kindedValueLabel } from '@/lib/session-labels';
import { type WalkNoyauSite, takenCard } from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import {
  type SessionConversationBubble,
  type SessionNoyauReading,
  type SessionStatus,
  type SessionVariableKind,
  type SessionWalkPage,
  noyauOf,
} from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import { MessageCircle } from 'lucide-react';

/**
 * One page the session traversed (§12.2): its rank, its name, its clock, what
 * was typed on it, and the button that left it.
 *
 * ## The values are shown HERE and nowhere else
 *
 * That is the whole reason the reader of §12.2 became a walk: « un écran qui
 * n'ouvrait que la conversation montrait les valeurs saisies loin de l'endroit
 * où elles avaient été tapées ». So a page carries its own pairs, in the order
 * the server grouped them, and the screen has no second list of variables.
 *
 * ## The rank is the rank in the WALK, not in the timeline
 *
 * « Page 2 » is the second page this session entered, and a page entered twice
 * gets two ranks. A number read off the frozen experience would answer a
 * different question - where the page sits in the design - which is the one this
 * screen is not about.
 *
 * ## A page played in the conversation says so (prolongation.md §7, issue #186)
 *
 * « Jouée dans la conversation » - a pill in the header and a line in the body
 * that leads to its bubble in the Conversation screen (`#bulle-<visite>`, which
 * that screen scrolls to and lights). Everything else reads as on any page: the
 * values, the card taken, the button that left it. The bubble is matched to the
 * card by the VISIT, the rank in the walk both screens print as « Page 7 ».
 *
 * A page with no value is not an empty card: it says so, because a card with a
 * header and nothing under it reads as a card that failed to load - unless it
 * carries a Noyau block (noyau.md §9.1, #152), whose cards or portrait are then
 * what the page shows.
 */

export interface WalkPageCardProps {
  readonly page: SessionWalkPage;
  /** 1-based rank in the walk. */
  readonly rank: number;
  /** Only `pageExit` reads it: it separates « terminée » from « encore là ». */
  readonly status: SessionStatus;
  /**
   * The Noyau blocks this page carried, the choice first (`noyauSitesOfPage`),
   * and the reading they are drawn from; absent on every other page.
   */
  readonly noyau?: {
    readonly sites: readonly WalkNoyauSite[];
    readonly reading: SessionNoyauReading;
  } | null;
  /**
   * The nature of each variable the inventory declares, by the key it is stored
   * under (#176): what lets a Note read « 4 / 5 » where the walk's own values
   * carry no kind. Absent, every value reads as it is stored.
   */
  readonly kinds?: ReadonlyMap<string, SessionVariableKind>;
  /** Ringed a moment: the card a line of the Variables tab led back to (#172). */
  readonly flash?: boolean;
  /**
   * The bubble of this visit when the page was played IN the conversation
   * (#186), with the session whose Conversation screen the line leads to;
   * absent on every other page.
   */
  readonly bubble?: {
    readonly sessionId: string;
    readonly bubble: SessionConversationBubble;
  } | null;
}

export function WalkPageCard({
  page,
  rank,
  status,
  noyau = null,
  kinds,
  flash = false,
  bubble = null,
}: WalkPageCardProps) {
  // The choice is what the page was LEFT by, so its card taken names the exit.
  const choice = noyau?.sites.find((site) => site.choice !== null)?.choice ?? null;
  const taken = choice === null ? null : takenCard(choice);
  const exit = pageExit(page, status, taken === null ? null : noyauOf(taken.noyau).name);

  return (
    <article
      data-testid={SESSIONS_TEST_IDS.walkPage}
      data-page-id={page.pageId}
      // The index of the visit, which the Variables tab leads back to (#172).
      data-visit={rank - 1}
      data-flash={flash ? 'true' : undefined}
      className={cn(
        'overflow-hidden rounded-md border border-gray-200 bg-white',
        flash ? 'pms-flash' : null,
      )}
    >
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-gray-100 border-b bg-gray-50 px-4 py-2.5">
        <span className="font-bold text-gray-500 text-micro uppercase tracking-wide">
          Page {rank}
        </span>
        <h3 className="font-bold text-gray-900 text-sm">{page.name}</h3>
        {bubble === null ? null : (
          <span
            data-testid={SESSIONS_TEST_IDS.walkBubble}
            className="inline-flex items-center gap-1 rounded-full bg-white px-2 font-semibold text-bleu-nuit text-micro ring-1 ring-bleu-nuit/20 ring-inset"
          >
            <MessageCircle aria-hidden="true" className="size-3" />
            jouée dans la conversation
          </span>
        )}
        <span className="ml-auto whitespace-nowrap text-caption text-gray-600 tabular-nums">
          {pageWindowLabel(page)}
        </span>
      </header>

      <div className="flex flex-col gap-2.5 px-4 py-3">
        {noyau?.sites.map((site) => (
          <WalkNoyauSection key={site.blockId} site={site} noyau={noyau.reading} />
        ))}

        {page.values.length === 0 ? (
          noyau === null ? (
            <p className="text-caption text-gray-600">Aucune saisie sur cette page.</p>
          ) : null
        ) : (
          <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-5 gap-y-1 text-caption">
            {page.values.map((value) => (
              <div key={value.blockId} className="contents">
                <dt className="text-gray-600">{value.name}</dt>
                <dd className="break-words font-semibold text-gray-900">
                  {isTicked(value.value) ? (
                    <span aria-hidden="true" className="mr-1 text-vert-deep">
                      ✓
                    </span>
                  ) : null}
                  {kindedValueLabel(value.value, kinds?.get(value.blockId))}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {bubble === null ? null : (
          <p className="text-caption text-gray-600">
            {isBubbleFailed(bubble.bubble)
              ? 'L’appel de la bulle a échoué : la page s’est jouée sans bulle'
              : 'Amenée par une bulle de l’interviewer'}
            {' · '}
            {costLabel(bubble.bubble.costEur)}
            {' · '}
            <Link
              data-testid={SESSIONS_TEST_IDS.walkBubbleLink}
              to="/admin/sessions/$sessionId/protocole"
              params={{ sessionId: bubble.sessionId }}
              hash={bubbleAnchor(bubble.bubble.visitIndex)}
              className="font-semibold text-bleu-nuit underline underline-offset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Voir la bulle dans la conversation
            </Link>
          </p>
        )}

        <p className="text-caption text-gray-600">
          {exit.text}
          {exit.emphasis === null ? null : (
            <>
              {' '}
              <b className="font-semibold text-gray-900">{exit.emphasis}</b>
            </>
          )}
        </p>
      </div>
    </article>
  );
}
